import { useRef, useState } from "react";

import useAxios from "@hooks/use-axios";
import { toastify } from "@helpers/toast";
import {
    INVOICE_ENDPOINTS,
    INVOICE_UPLOAD_TYPE
} from "../misc/constants";
import { normalizeInvoiceDocument } from "@panel/shared/invoice/misc/helpers";
import {
    previewKindFor,
    validateInvoiceFile
} from "../misc/helpers";

const payloadOf = (res) => res?.data ?? res;

/**
 * The earnings table's tax invoice upload. Picking a file uploads it and stops
 * there; attaching it to the invoice is a second, explicit act, so cancelling
 * the confirmation leaves the invoice exactly as it was.
 * @param root0
 * @param root0.publisherId
 * @param root0.onAttached called with (rowId, document) once the PATCH lands
 */
const useInvoiceUpload = ({ publisherId, onAttached }) => {
    const { axiosInstance: uploadInstance, loading: uploading } = useAxios({ base: "base", showAlert: false });
    const { axiosInstance: saveInstance, loading: saving } = useAxios({ base: "affiliate", showAlert: false });

    // One hidden input serves every row — the row it was opened for is held in
    // `target` until the file lands.
    const inputRef = useRef(null);
    const [target, setTarget] = useState(null);
    // The uploaded-but-not-yet-attached file. Its presence is what opens the
    // confirmation dialog, and dropping it is what cancelling means.
    const [staged, setStaged] = useState(null);

    const uploadFile = async (file) => {
        try {
            const formData = new FormData();
            formData.append("file", file);
            const res = await uploadInstance.post(INVOICE_ENDPOINTS.upload, formData, {
                params: { type: INVOICE_UPLOAD_TYPE }
            });

            const uploaded = (res.data || [])[0];
            if (!uploaded?.url) {
                toastify({ title: "Upload failed. Please try again.", type: "error" });
                return null;
            }

            return {
                url         : uploaded.url,
                name        : uploaded.originalName || file.name,
                originalName: uploaded.originalName || file.name,
                size        : uploaded.size ?? file.size
            };
        } catch {
            toastify({ title: "Upload failed. Please try again.", type: "error" });
            return null;
        }
    };

    const handleFile = async (file) => {
        const row = target;
        if (!row) return;

        const error = validateInvoiceFile(file);
        if (error) {
            toastify({ title: error, type: "error" });
            setTarget(null);
            return;
        }

        const uploaded = await uploadFile(file);
        setTarget(null);
        if (!uploaded) return;

        setStaged({ ...uploaded, row, file, previewKind: previewKindFor(file) });
    };

    const openPicker = (row) => {
        if (uploading) return;
        setTarget(row);
        inputRef.current?.click();
    };

    const inputProps = {
        ref     : inputRef,
        type    : "file",
        onChange: (e) => {
            const file = Array.from(e.target.files || [])[0];
            // Reset first so picking the same filename again still fires onChange.
            e.target.value = "";
            if (file) handleFile(file);
            else setTarget(null);
        }
    };

    const handleConfirm = async () => {
        if (!publisherId || !staged?.row?.invoiceId) return;

        try {
            const res = await saveInstance.patch(
                INVOICE_ENDPOINTS.document(publisherId, staged.row.invoiceId),
                {
                    invoice_url : staged.url,
                    invoice_meta: {
                        name         : staged.name,
                        original_name: staged.originalName,
                        size         : staged.size
                    }
                }
            );

            // The response carries the stored document back, timestamps
            // included — use it rather than what was uploaded, so the row
            // shows what the server actually kept.
            const document = normalizeInvoiceDocument(payloadOf(res) || {})
                || { url: staged.url, name: staged.originalName, size: staged.size, uploadedAt: null };

            onAttached(staged.row.id, document);
            setStaged(null);
            toastify({ title: "Tax invoice attached successfully.", type: "success" });
        } catch {
            toastify({ title: "Could not attach the tax invoice. Please try again.", type: "error" });
        }
    };

    // Nothing to undo: the object stays in the bucket but was never attached
    // to the invoice, and the attached document never changed.
    const handleCancel = () => setStaged(null);

    return {
        inputProps,
        openPicker,
        uploading,
        // Only the row being uploaded for shows a spinner.
        uploadingRowId: uploading ? target?.id ?? null : null,
        confirmModal  : {
            open       : Boolean(staged),
            staged,
            // What the confirmation warns about depends on whether this
            // replaces a document the brand can already see.
            isReplacing: Boolean(staged?.row?.document),
            invoiceRef : staged?.row?.invoice_ref || "",
            saving,
            onConfirm  : handleConfirm,
            onCancel   : handleCancel
        }
    };
};

export default useInvoiceUpload;
